import { type ChangeEvent, useCallback, useMemo } from 'react'

import { Retention } from '@api'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { FormController } from '@/components/ui/form/form-controller.tsx'
import { numberConverter } from '@/components/ui/form/utils.ts'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import {
  type FormValues,
  schema,
} from '@/dialogs/AdditionalCostDetailsDialog/validation.ts'
import type { DialogProps } from '@/dialogs/types'
import { useAccountingAdditionalCostTypes } from '@/modules/accounting/hooks/useAccountingAdditionalCostTypes.ts'
import { useSaveAdditionalCost } from '@/modules/accounting/hooks/useSaveAdditionalCost.ts'
import type { AccountingCostBody } from '@/modules/accounting/types.ts'
import { RETENTION_LABELS } from '@/modules/accounting/utils.ts'
import { handleError } from '@/utils/error'

interface Props extends DialogProps {
  data: AccountingCostBody
}

export const AdditionalCostDetailsDialog = ({
  isOpen,
  onClose,
  data,
}: Props) => {
  const { saveCost } = useSaveAdditionalCost()
  const { data: costTypes } = useAccountingAdditionalCostTypes()
  const form = useForm({
    defaultValues: {
      id: data.id,
      name: data.name,
      amount: data.amount,
      retention: data.retention,
      typeId: data.typeId,
    },
    resolver: zodResolver(schema),
  })
  const {
    formState: { isDirty, isSubmitting, errors },
  } = form

  const retention = form.watch('retention')
  const typeId = form.watch('typeId')

  const costType = useMemo(() => {
    return costTypes?.find((type) => type.id === typeId)
  }, [costTypes, typeId])

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      try {
        await saveCost(values)
        onClose()
      } catch (error) {
        handleError(error, 'Failed to save cost, please try again')
        console.error('Failed to save cost:', error)
      }
    },
    [onClose, saveCost],
  )

  const handleAmountChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      form.setValue('amount', +event.target.value || 0, {
        shouldValidate: true,
        shouldDirty: true,
      })
    },
    [form],
  )

  const handleNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      form.setValue('name', event.target.value, {
        shouldValidate: true,
        shouldDirty: true,
      })
    },
    [form],
  )

  const handleRetentionChange = useCallback(
    (value: string) => {
      form.setValue('retention', value as Retention, {
        shouldValidate: true,
        shouldDirty: true,
      })
    },
    [form],
  )

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <Dialog.Header>
            <Dialog.Title>
              {data.id ? 'Edit' : 'Set'} {costType?.name} Costs
            </Dialog.Title>
          </Dialog.Header>

          <div className="mt-5">
            <Controller
              name="name"
              control={form.control}
              render={({ field }) => (
                <FormController>
                  <FormController.Label>
                    {costType?.name} Name
                  </FormController.Label>
                  <FormController.Input>
                    <TextField
                      value={field.value}
                      onChange={handleNameChange}
                    />
                  </FormController.Input>
                  <FormController.Error>
                    {errors.name?.message}
                  </FormController.Error>
                </FormController>
              )}
            />
          </div>
          <Separator className="my-4" />
          <Controller
            name="amount"
            control={form.control}
            render={({ field }) => (
              <FormController>
                <FormController.Label>
                  {costType?.name} Fee
                </FormController.Label>
                <FormController.Input>
                  <TextField
                    type="number"
                    prefix="$"
                    suffix={
                      <div className="w-[120px]">
                        <Select
                          value={retention}
                          onValueChange={handleRetentionChange}
                        >
                          <SelectTrigger>
                            <SelectValue className="capitalize" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              {Object.values(Retention).map((t) => (
                                <SelectItem
                                  key={t}
                                  value={t}
                                >
                                  {RETENTION_LABELS[t]}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </div>
                    }
                    value={numberConverter.output(field.value)}
                    onChange={handleAmountChange}
                  />
                </FormController.Input>
                <FormController.Error>
                  {errors.amount?.message}
                </FormController.Error>
              </FormController>
            )}
          />

          <Dialog.Footer className="grid grid-cols-2 gap-3">
            <Button
              onClick={onClose}
              variant="outline"
              type="button"
            >
              Cancel
            </Button>
            <Button
              isLoading={isSubmitting}
              disabled={!isDirty}
            >
              Continue
            </Button>
          </Dialog.Footer>
        </form>
      </FormProvider>
    </Dialog>
  )
}
